import { I18n } from 'react-redux-i18n';
import cx from 'classnames';
import debounce from 'lodash/debounce';

import ActionsButton from '@common/components/ActionsButton';
import { getTestAttributes } from '@common/utils/tests';
import { InfoIcon } from '@common/components/info-icon';
import { Loading } from "@common/components/loading";
import { Flex } from "@common/components/Flex";

import { MODULE_NAME } from '../../individuals';
import { ProfileSelector } from './components/profile-selector';
import { generateLetter, getFields, getFolders, openDatasheet, openPedigree, openTestResults } from '../../actions/individuals';
import { IndividualRenaming } from './components/individual-renaming';
import { createBatchLetters } from '../../actions/letters';
import { NewQuestionnaire } from './components/new-questionnaire';
import { CONTEXT_TYPE } from '../../constants/context';
import { NewTable, NewTableColumnPredefinedActionType, NewTableHeaderPredefinedActionType } from '@common/components/new-table';

import './individuals-table.styles.scss';

export class IndividualsTable extends React.Component {
  fieldType = {
    date: 2,
    dateTime: 222,
  };
  dateFieldTypes = [this.fieldType.date, this.fieldType.dateTime];
  profileSelectorTriggerType = {
    defaultColumns: 'defaultColumns',
    currentColumns: 'currentColumns',
  };
  profileSelectorTrigger;
  featureType = {
    setDefaultForAllUsers: 1,
    setDefaultForSpecificUsers: 2,
    applyCurrentColumnsToAllUsers: 3,
    applyCurrentColumnsToSpecificProfiles: 4,
    restoreColumnsToDefault: 5,
    addColumns: 6,
    deleteColumn: 7
  };

  get columns() {
    const defaultWidth = 100;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: 'Actions', 
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: ({ row: { original } }) => {
          return <ActionsButton 
            id={original.id} 
            data={original} 
            menuItems={this.getActions(original.invitesCount)}
            setCurrent={this.handleSetCurrentSelectedRow}
          />
        },
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...this.props.columns.map((x) => ({
        id: this.props.getColumnId(x),
        accessorKey: x.field.id.toString(),
        enableSorting: true,
        minSize: 20,
        size: x.width || defaultWidth,
        header: x.field.name,
        filterProps: { 
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ];
  }

  state = {
    profileSelectorOpened: false,
    fieldChooserOpened: false,
    allowedFeature: {},
    renamingIndividual: {
      opened: false,
      defaultValue: '',
      id: null,
    },
    newQuestionnaireDialogProps: {
      individualName: '',
      individualId: null,
      opened: false,
    },
  };

  componentDidMount() {
    this.props.getAllowedFeatures().then((allowedFeature) => {
      this.setState((x) => ({ ...x, allowedFeature }));
    });
  }

  handleNewQuestionnaireOpen = (id, data) => {
    this.setState((x) => ({
      ...x,
      newQuestionnaireDialogProps: {
        ...x.newQuestionnaireDialogProps,
        individualName: data.name,
        individualId: data.id,
        opened: true,
      },
    }));
  }

  handleNewQuestionnaireSubmit = (url) => {
    window.location.href = url;
  }

  closeNewQuestionnaireDialog = () => {
    this.setState((x) => ({
      ...x,
      newQuestionnaireDialogProps: {
        ...x.newQuestionnaireDialogProps,
        individualName: '',
        individualId: null,
        opened: false,
      },
    }));
  }

  handleSetCurrentSelectedRow = (id) => {
    this.props.onSelectedIndividualsChange([id]);
  }

  handleSelectedItemsChange = (allSelectedItemsId) => {
    const { onSelectedIndividualsChange } = this.props;

    onSelectedIndividualsChange(allSelectedItemsId);
  }

  getTrProps = (state, rowInfo) => {
    const { index } = rowInfo;

    return {
      ...getTestAttributes(MODULE_NAME, 'item', 'table-row', index)
    };
  }

  getTheadThProps = (tableData, rowData, { id }) => getTestAttributes(MODULE_NAME, 'item', 'table-header-cell', id);

  getTdProps = (tableData, { index }, { id }) => getTestAttributes(MODULE_NAME, 'item', 'table-cell', `${index}-${id}`);

  handleColumnResize = (columns) => {
    this.props.onResize(columns);
  }

  openProfileSelector = () => {
    this.setState((x) => ({ ...x, profileSelectorOpened: true }));
  }

  closeProfileSelector = () => {
    this.setState((x) => ({ ...x, profileSelectorOpened: false }));
  }

  handleSetDefaultForSpecificUsers = () => {
    this.openProfileSelector();
    this.profileSelectorTrigger = this.profileSelectorTriggerType.defaultColumns;
  }

  handleApplyCurrentColumnsToSpecificUsers = () => {
    this.openProfileSelector();
    this.profileSelectorTrigger = this.profileSelectorTriggerType.currentColumns;
  }
  
  handleColumnAdd = ({ field, columnId }) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);

    const column = {
      width: 100,
      field: {
        id: field.fieldId,
        name: field.fieldName,
        context: field.fieldContext,
        type: field.fieldTypeId,
        system: field.systemField
      },
      sortOrder: 0,
      sortAsc: undefined

    };
    const columnsCopy = [...this.props.columns];
    
    columnsCopy.splice(columnIndex, 0, column);
    
    return this.props.onColumnsChange(columnsCopy);
  }

  handleColumnRemove = (columnId) => {
    const columnIndex = this.columns.findIndex((x) => x.id === columnId);
    const columnsCopy = [...this.props.columns];
    const actionsColumn = 1;

    columnsCopy.splice(columnIndex - actionsColumn, 1);

    return this.props.onColumnsChange(columnsCopy);
  }

  closeFieldChooser = async () => {
    this.setState((x) => ({ ...x, fieldChooserOpened: false }));
  }

  handleProfileSelectorSubmit = async (users) => {
    const handlerConfig = {
      [this.profileSelectorTriggerType.currentColumns]: this.props.onApplyCurrentColumnsToSpecificUsers,
      [this.profileSelectorTriggerType.defaultColumns]: this.props.onSetDefaultForSpecificUSers,
    };
    const handle = handlerConfig[this.profileSelectorTrigger];
    await handle(users);
    this.closeProfileSelector();
  }

  handleFieldChange = (field) => {

  }

  handleOpenDatasheet = (id) => {
    openDatasheet(id);
  }

  handleOpenPedigree = (id) => {
    openPedigree(id);
  }

  handleOpenTestResults = (id) => {
    openTestResults(id);
  }

  handleRenameIndividualClick = (id, individual) => {
    this.setState((x) => ({
      ...x,
      renamingIndividual: {
        defaultValue: individual.name,
        opened: true,
        id: individual.id,
      },
    }));
  }

  closeRenamingIndividual = () => {
    this.setState((x) => ({
      ...x,
      renamingIndividual: {
        defaultValue: '',
        opened: false,
        id: null,
      },
    }));
  }

  handleRenameIndividual = async (name) => {
    await this.props.onRenameIndividual(this.state.renamingIndividual.id, name);
    this.closeRenamingIndividual();
    this.props.showMessage({
      header: I18n.t('Information Message'),
      content: <><InfoIcon />  {I18n.t('Individual successfully renamed')}</>,
    });
  }

  handleGenerateLetter = (id, individual) => {
    generateLetter(individual.id);
  }

  handleNewInviteOpen = (individualId) => {
    this.props.onNewInviteOpen(individualId);
  }

  handleQuestionnaireOpen = (id, data) => {
    this.props.onQuestionnaireOpen({
      id: data.id,
      name: data.name,
    });
  }

  getActions(invitesCount) {
    invitesCount = invitesCount ? Number(invitesCount) : 0;

    return [
      { label: 'Open Datasheet', onClick: this.handleOpenDatasheet },
      { label: 'Open Pedigree', onClick: this.handleOpenPedigree },
      { label: 'Open Test Results', onClick: this.handleOpenTestResults },
      { label: 'Rename Individual', onClick: this.handleRenameIndividualClick },
      { label: 'New Invite', onClick: this.handleNewInviteOpen, },
      { label: 'Launch New Questionnaire', onClick: this.handleNewQuestionnaireOpen, },
      { label: 'Open Questionnaire', onClick: this.handleQuestionnaireOpen, condition: invitesCount > 0 },
      { label: 'Generate Letter', onClick: this.handleGenerateLetter },
    ].filter(({ condition = true }) => condition).map((x) => ({ ...x, label: I18n.t(x.label) }));
  }

  get columnActions() {
    const { onSetDefaultForAllUSers, onApplyCurrentColumnsToAllUsers, onRestoreColumnToDefault } = this.props;

    return [
      { type: NewTableColumnPredefinedActionType.filters },
      { feature: this.featureType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), className: 'IndividualsTable__column-action-top-divider', onClick: onSetDefaultForAllUSers },
      { feature: this.featureType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: this.handleSetDefaultForSpecificUsers },
      { feature: this.featureType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: onApplyCurrentColumnsToAllUsers },
      { feature: this.featureType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: this.handleApplyCurrentColumnsToSpecificUsers },
      { feature: this.featureType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: onRestoreColumnToDefault },
      { feature: this.featureType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'IndividualsTable__column-action-top-divider' },
      { feature: this.featureType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
    ].filter(({ feature }) => feature ? this.state.allowedFeature[feature] : true).map(({ feature, ...otherProps }) => otherProps);
  }

  get headerActions() {
    const { onSetDefaultForAllUSers, onApplyCurrentColumnsToAllUsers, onRestoreColumnToDefault } = this.props;

    return [
      { feature: this.featureType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), onClick: onSetDefaultForAllUSers },
      { feature: this.featureType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: this.handleSetDefaultForSpecificUsers },
      { feature: this.featureType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: onApplyCurrentColumnsToAllUsers },
      { feature: this.featureType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: this.handleApplyCurrentColumnsToSpecificUsers },
      { feature: this.featureType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: onRestoreColumnToDefault },
      { feature: this.featureType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'IndividualsTable__column-action-top-divider' },
    ].filter(({ feature }) => feature ? this.state.allowedFeature[feature] : true).map(({ feature, ...otherProps }) => otherProps);
  }

  handleGetFields = ({ searchKeyword, folderId, system, }) => {
    const noFolderSelected = 0;

    return getFields({ 
      searchQuery: searchKeyword, 
      folderId: folderId || noFolderSelected, 
      system,
      contextId: CONTEXT_TYPE.individual
    });
  }

  handleBatchLetters = () => {
    if (this.props.selectedIndividualsId?.length) {
      createBatchLetters(this.props.selectedIndividualsId);
    }
  }

  get rowContextMenuActions() {
    return [
      {
        label: I18n.t('Batch Letters'),
        onClick: this.handleBatchLetters
      }
    ];
  }

  getData = (params) => {
    const { filtering, sorting, pagination } = params;

    return this.props.getData({
      filtering: filtering,
      pagination,
      sorting: {
        ...sorting,
        index: this.columns.findIndex((x) => x.id === sorting.id),
      },
    });
  }

  render() {
    const {
      className, 
      data,
      totalCount,
      getData, 
      loadMetadata, 
      filters, 
      getProfiles, 
      showErrorMessage, 
      selectedIndividualsId,
      loading
    } = this.props;

    return (
      <div className={cx('IndividualsTable', className)}>
        {
          loading && (
            <Flex width="100%" justify="center" alignItems="center" className="IndividualsTable__loading-content">
              <Loading size={25}/>
            </Flex>
          )
        }
        {this.state.newQuestionnaireDialogProps.opened && (
          <NewQuestionnaire
            individualName={this.state.newQuestionnaireDialogProps.individualName}
            individualId={this.state.newQuestionnaireDialogProps.individualId}
            showErrorMessage={showErrorMessage}
            onClose={this.closeNewQuestionnaireDialog}
            onSubmit={this.handleNewQuestionnaireSubmit}
          />
        )}
        {this.state.profileSelectorOpened && (
          <ProfileSelector 
            getData={getProfiles}
            onClose={this.closeProfileSelector}
            onSubmit={this.handleProfileSelectorSubmit}
            showErrorMessage={showErrorMessage}
          />
        )}
        {this.state.renamingIndividual.opened && (
          <IndividualRenaming 
            defaultValue={this.state.renamingIndividual.defaultValue}
            onSubmit={this.handleRenameIndividual}
            onClose={this.closeRenamingIndividual}
            showErrorMessage={showErrorMessage}
          />
        )}
        <NewTable 
          infiniteScroll
          disableAutoInit
          classes={{
            root: 'IndividualsTable__component'
          }}
          columnActions={this.columnActions}
          headerActions={this.headerActions}
          columnsResizing={{
            onChange: this.handleColumnResize,
          }}
          //   getTrProps: this.getTrProps, // TODO
          //   getTheadThProps: this.getTheadThProps,
          //   getTdProps: this.getTdProps,
          rowActions={this.rowContextMenuActions}
          selection={{
            ids: selectedIndividualsId,
            onChange: this.handleSelectedItemsChange
          }}
          moduleName={`${MODULE_NAME}_individuals_table`}
          filters={{
            operators: filters,
          }}
          noDataMessage={I18n.t('There no individuals to display')}
          itemUniqProperty="id"
          data={data}
          columns={this.columns}
          totalItemsCount={totalCount}
          loadMetadata={{
            ...loadMetadata,
            sorting: {
              direction: loadMetadata.sorting.direction,
              id: loadMetadata.sorting.id ?? this.columns[1]?.id
            },
          }}
          columnsManagement={{
            onAdd: this.handleColumnAdd,
            onRemove: this.handleColumnRemove,
            getFolders,
            getFields: this.handleGetFields,
          }}
          onLoadData={this.getData}
        />  
      </div>
    );
  }
}